/**
 * /exercise/topic/:nodeId — generic node-scoped drill.
 *
 * Serves curriculum nodes that have a working `generate()` case but no bespoke
 * skill-drill screen (chromatic degrees, chromatic intervals, slash chords,
 * secondary dominants, modulation — see `TOPIC_DRILL_NODES`). Built entirely on
 * the renderer protocol: seed a queue from `expandNode(node)`, then
 * `generate → play → getRenderer → gradeExercise`, exactly like `Placement.tsx`
 * and `ReviewSession.tsx`. No per-exercise-type code lives here — the registry
 * dispatches Solfège vs multiple-choice.
 *
 * Modes (from `?mode=`): listen (hear + reveal, no grade), practice (graded,
 * no FSRS write), review (graded + `applyReview`). Mirrors the write policy of
 * the bespoke drills (ScaleExercise etc.).
 */

import { type ReactNode, useMemo, useRef, useState, useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  generate,
  gradeExercise,
  type AudioPlan,
  type Exercise,
  type ExerciseAnswer,
  type GradingResult,
} from "@music-app/exercise-generator";
import { createAudioPlayer, type AudioPlayer } from "@music-app/audio-engine-web";
import { ExerciseCard, StagePips, FeedbackBox, AudioStatusIndicator } from "@music-app/ui-components";
import {
  MVP_NODES,
  expandNode,
  isTopicDrillNode,
  exerciseReviewSource,
  ratingFromResponse,
  type Item,
} from "@music-app/learning-system";
import { useCardsStore, usePrefsStore, recordReview as dbRecordReview } from "@music-app/persistence";
import { filterByPrefs } from "./session-prefs.js";
import { getRenderer } from "../lib/exercise-renderers/registry.js";
import { useListenSession } from "../lib/use-listen-session.js";
import { useExerciseSession } from "../lib/use-exercise-session.js";
import { PageHeader } from "../components/PageHeader.js";
import { buttonClasses } from "../components/Button.js";
import { type ExerciseMode, MODE_COLOR } from "../components/ModeSwitcherBar.js";

type AudioStatus = "loading" | "ready" | "synth-fallback" | "error";
type Phase = "intro" | "running" | "done";
type ItemPhase = "playing" | "thinking" | "awaiting" | "graded" | "revealed";

const CADENCE_PIP_COUNT = 4;
const SESSION_LENGTH_PRESETS = [5, 10, 15, 20, 30];
const DEFAULT_SESSION_LENGTH = 10;
const TOPIC_MODES: ExerciseMode[] = ["listen", "practice", "review"];

function shuffle<T>(arr: readonly T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = out[i]!;
    out[i] = out[j]!;
    out[j] = tmp;
  }
  return out;
}

/** Fill a session of `length` rounds from a node's items — reshuffle each pass
 *  so a small node (e.g. 4 items) still fills a 10-round session without an
 *  immediate repeat run. */
function buildQueue(items: Item[], length: number): Item[] {
  if (items.length === 0) return [];
  const queue: Item[] = [];
  while (queue.length < length) queue.push(...shuffle(items));
  return queue.slice(0, length);
}

export function TopicDrillPage(): ReactNode {
  const { nodeId } = useParams<{ nodeId: string }>();
  const [searchParams] = useSearchParams();
  const rawMode = searchParams.get("mode") as ExerciseMode | null;
  const mode: ExerciseMode = rawMode && TOPIC_MODES.includes(rawMode) ? rawMode : "practice";
  const from = searchParams.get("from") ?? undefined;
  const listenMode = mode === "listen";

  const node = useMemo(() => MVP_NODES.find((n) => n.id === nodeId), [nodeId]);

  const includeLeadingTone = usePrefsStore((s) => s.includeLeadingTone);
  const instrument = usePrefsStore((s) => s.instrument);
  const applyReview = useCardsStore((s) => s.applyReview);

  const { recordPlay } = useListenSession(nodeId ?? "topic", listenMode, nodeId);
  const { recordItem } = useExerciseSession(
    nodeId ?? "topic",
    mode === "review" ? "practice" : "drill",
    !listenMode,
  );

  const nodeItems = useMemo(
    () => (node ? filterByPrefs(expandNode(node), { includeLeadingTone }) : []),
    [node, includeLeadingTone],
  );

  const [sessionLength, setSessionLength] = useState(DEFAULT_SESSION_LENGTH);
  const [phase, setPhase] = useState<Phase>("intro");
  const [queue, setQueue] = useState<Item[]>([]);
  const [idx, setIdx] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);

  const [exercise, setExercise] = useState<Exercise | null>(null);
  const [itemPhase, setItemPhase] = useState<ItemPhase>("playing");
  const [pendingAnswer, setPendingAnswer] = useState<ExerciseAnswer | null>(null);
  const [gradingResult, setGradingResult] = useState<GradingResult | null>(null);
  const awaitingStartRef = useRef<number>(0);

  const [audioStatus, setAudioStatus] = useState<AudioStatus>("loading");
  const [pianoProgress, setPianoProgress] = useState<number | undefined>(undefined);

  const [activePip, setActivePip] = useState<number | null>(null);
  const [donePips, setDonePips] = useState<number[]>([]);
  const bassIndexRef = useRef(0);

  const playerRef = useRef<AudioPlayer | null>(null);
  const ensureAudioPromiseRef = useRef<Promise<AudioPlayer> | null>(null);

  useEffect(() => () => { playerRef.current?.destroy(); playerRef.current = null; }, []);

  const ensureAudio = (): Promise<AudioPlayer> => {
    // Re-init a cached player too: init() is idempotent and resumes a suspended
    // AudioContext (browsers suspend between gestures / on auto-advance rounds).
    if (playerRef.current) {
      const p = playerRef.current;
      return p.init().then(() => p);
    }
    if (ensureAudioPromiseRef.current) return ensureAudioPromiseRef.current;
    const promise = (async () => {
      const player = createAudioPlayer();
      await player.init();
      playerRef.current = player;
      if (instrument === "synth") { setAudioStatus("ready"); return player; }
      setAudioStatus("loading");
      setPianoProgress(0);
      try {
        await player.loadInstrument(instrument, {
          onProgress: (loaded, total) => setPianoProgress(total > 0 ? loaded / total : 0),
        });
        try { player.setInstrument(instrument); setAudioStatus("ready"); }
        catch { setAudioStatus("synth-fallback"); }
      } catch { setAudioStatus("synth-fallback"); }
      finally { setPianoProgress(undefined); }
      return player;
    })();
    ensureAudioPromiseRef.current = promise;
    promise.finally(() => { ensureAudioPromiseRef.current = null; });
    return promise;
  };

  const playAudio = async (player: AudioPlayer, audio: AudioPlan) => {
    setDonePips([]);
    setActivePip(null);
    bassIndexRef.current = 0;
    await player.play(audio, {
      onNoteStart: (e) => { if (e.voice === "bass") { const i = bassIndexRef.current; bassIndexRef.current = i + 1; setActivePip(i); } },
      onNoteEnd: (e) => { if (e.voice === "bass") { setActivePip(null); setDonePips((d) => [...d, d.length]); } },
    });
    setActivePip(null);
  };

  // Reads from the passed queue array — never component state, which is stale
  // on the synchronous first call right after setQueue(). ensureAudio() each
  // round also resumes a suspended AudioContext (the "no sound" path).
  const playItemAt = async (q: Item[], itemIndex: number) => {
    const item = q[itemIndex];
    if (!item) return;
    const player = await ensureAudio();
    const ex = generate(item, Date.now());
    setExercise(ex);
    setPendingAnswer(null);
    setGradingResult(null);
    setItemPhase("playing");
    await playAudio(player, ex.audio);
    if (listenMode) {
      recordPlay(ex.itemId);
      setItemPhase("thinking");
    } else {
      recordItem(ex.itemId);
      awaitingStartRef.current = performance.now();
      setItemPhase("awaiting");
    }
  };

  const handleStart = async () => {
    const q = buildQueue(nodeItems, sessionLength);
    if (q.length === 0) { setPhase("done"); return; }
    await ensureAudio();
    setQueue(q);
    setIdx(0);
    setCorrectCount(0);
    setPhase("running");
    await playItemAt(q, 0);
  };

  const replayAudio = async () => {
    if (!exercise || itemPhase === "playing") return;
    const player = await ensureAudio();
    const prev = itemPhase;
    setItemPhase("playing");
    await playAudio(player, exercise.audio);
    if (prev === "awaiting") awaitingStartRef.current = performance.now();
    setItemPhase(prev);
  };

  const advance = () => {
    const nextIdx = idx + 1;
    if (nextIdx >= queue.length) { setPhase("done"); setExercise(null); return; }
    setIdx(nextIdx);
    void playItemAt(queue, nextIdx);
  };

  const reveal = () => {
    if (!exercise || !listenMode || itemPhase !== "thinking") return;
    // Mark the correct option in the answer surface (renderers highlight the
    // correct id whenever gradingResult is non-null).
    setGradingResult({ correct: true, explanation: exercise.meta.explanation ?? "" });
    setItemPhase("revealed");
    const teach = exercise.meta.teachAudio;
    if (teach) void ensureAudio().then((p) => playAudio(p, teach));
  };

  const handleSubmit = (answer: ExerciseAnswer) => {
    if (!exercise || itemPhase !== "awaiting") return;
    const result = gradeExercise(exercise, answer);
    const responseMs = performance.now() - awaitingStartRef.current;
    setPendingAnswer(answer);
    setGradingResult(result);
    setItemPhase("graded");
    if (result.correct) setCorrectCount((c) => c + 1);

    const rating = ratingFromResponse(result.correct, responseMs);
    const chosenAnswerId = !result.correct && answer.type === "option" ? answer.optionId : null;
    const source = exerciseReviewSource({ challengeMode: false, fsrsEnabled: mode === "review" });
    void dbRecordReview({
      itemId: exercise.itemId,
      sessionId: "",
      rating,
      correct: result.correct,
      responseMs,
      chosenAnswerId,
      source,
    });
    if (mode === "review") void applyReview(exercise.itemId, rating);

    if (!result.correct && exercise.meta.teachAudio && playerRef.current) {
      const teach = exercise.meta.teachAudio;
      const player = playerRef.current;
      window.setTimeout(() => { void playAudio(player, teach); }, 600);
    }
  };

  useEffect(() => {
    if (phase !== "running") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== "Space") return;
      if (itemPhase === "thinking") { e.preventDefault(); reveal(); }
      else if (itemPhase === "revealed" || itemPhase === "graded") { e.preventDefault(); advance(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, itemPhase, idx, queue.length, exercise]);

  /* ─── render ─────────────────────────────────────────────────────── */

  if (!nodeId || !node || !isTopicDrillNode(nodeId)) {
    return (
      <div className="max-w-2xl mx-auto py-9 px-8">
        <PageHeader eyebrow="Drill" title="Topic not found" subhead="This drill isn't available." />
        <Link to="/exercise" className={buttonClasses("secondary")}>← Exercises</Link>
      </div>
    );
  }

  const modeColor = MODE_COLOR[mode];
  const backHref = from ?? "/exercise";
  const backLabel = from ? "← Back" : "← Exercises";

  if (phase === "intro") {
    return (
      <div className="max-w-2xl mx-auto py-9 px-8">
        <PageHeader eyebrow="Drill" title={node.title} subhead="Focused practice on one topic." />
        <div className="mt-6 space-y-6">
          <div className="flex flex-wrap gap-2">
            {TOPIC_MODES.map((m) => (
              <Link
                key={m}
                to={`/exercise/topic/${nodeId}?mode=${m}${from ? `&from=${from}` : ""}`}
                replace
                className="px-3 py-1.5 rounded-full font-mono text-[11px] uppercase tracking-[0.16em] border transition-colors"
                style={m === mode
                  ? { background: MODE_COLOR[m], color: "white", borderColor: MODE_COLOR[m] }
                  : { color: MODE_COLOR[m], borderColor: `${MODE_COLOR[m]}66` }}
              >
                {m}
              </Link>
            ))}
          </div>

          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-2">Session length</div>
            <div className="flex flex-wrap gap-2">
              {SESSION_LENGTH_PRESETS.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setSessionLength(n)}
                  className="px-3 py-1.5 rounded-md font-mono text-xs border transition-colors"
                  style={n === sessionLength
                    ? { background: modeColor, color: "white", borderColor: modeColor }
                    : { borderColor: "rgba(0,0,0,0.15)" }}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          <p className="text-sm text-inkSoft font-serif italic">
            {nodeItems.length} distinct item{nodeItems.length === 1 ? "" : "s"} in this topic.
          </p>

          <button type="button" onClick={() => void handleStart()} className={buttonClasses("primary")}>
            {listenMode ? "▶ Start listening →" : "▶ Start →"}
          </button>
        </div>
        <div className="mt-8">
          <Link to={backHref} className={buttonClasses("text")}>{backLabel}</Link>
        </div>
      </div>
    );
  }

  if (phase === "done") {
    return (
      <div className="max-w-2xl mx-auto py-9 px-8">
        <PageHeader eyebrow="Done" title={node.title} subhead="Session complete." />
        {!listenMode && (
          <p className="mt-6 font-serif text-lg text-ink">
            {correctCount} / {queue.length} correct.
          </p>
        )}
        <div className="mt-8 flex gap-3">
          <button type="button" onClick={() => setPhase("intro")} className={buttonClasses("primary")}>
            Again
          </button>
          <Link to={backHref} className={buttonClasses("secondary")}>{backLabel}</Link>
        </div>
      </div>
    );
  }

  // phase === "running"
  const currentItemNumber = idx + 1;
  const totalItems = queue.length;
  const isPlaying = itemPhase === "playing";
  const isThinking = itemPhase === "thinking";
  const isRevealed = itemPhase === "revealed";
  const isAwaiting = itemPhase === "awaiting";
  const isGraded = itemPhase === "graded";
  const correctChosen = isGraded && gradingResult?.correct === true;
  const correctLabel = exercise?.options.find((o) => o.id === exercise.correctAnswerId)?.label ?? "—";

  return (
    <div className="max-w-2xl mx-auto py-9 px-8">
      <div className="h-0.5 bg-ink/10 mb-6" role="progressbar" aria-valuenow={currentItemNumber} aria-valuemax={totalItems}>
        <div className="h-full transition-all duration-300" style={{ width: `${(currentItemNumber / totalItems) * 100}%`, background: modeColor }} />
      </div>
      <PageHeader
        eyebrow={mode}
        title={node.title}
        subhead={undefined}
        right={
          <AudioStatusIndicator
            status={audioStatus}
            progress={pianoProgress}
            instrumentLabel={audioStatus === "ready" ? instrument : audioStatus === "synth-fallback" ? "synth" : undefined}
          />
        }
      />

      <ExerciseCard
        context={exercise ? <>Key: <strong className="font-serif italic text-base text-ink">{exercise.meta.keyName ?? "—"}</strong></> : "—"}
        status={`${currentItemNumber} of ${totalItems}`}
        prompt={exercise?.prompt ?? "Listen…"}
        stagePips={isPlaying ? <StagePips count={CADENCE_PIP_COUNT} active={activePip} done={donePips} /> : undefined}
        controls={
          <div className="flex gap-2 flex-wrap">
            {!isPlaying && (
              <button type="button" onClick={() => void replayAudio()} className={buttonClasses("secondary")}>↻ Replay</button>
            )}
            {isThinking && (
              <button type="button" onClick={reveal} className={buttonClasses("primary")}>Reveal (Space)</button>
            )}
            {(isRevealed || isGraded) && (
              <button type="button" onClick={advance} className={buttonClasses("primary")}>
                {currentItemNumber >= totalItems ? "Finish" : "Next →"}
              </button>
            )}
          </div>
        }
        options={
          exercise
            ? getRenderer(exercise.exerciseType)({
                exercise,
                pendingAnswer,
                gradingResult,
                // Answerable only while awaiting in practice/review. In listen
                // the surface is shown but disabled; the reveal step marks the
                // correct option via a synthetic gradingResult.
                disabled: itemPhase !== "awaiting",
                onAnswerChange: setPendingAnswer,
                onSubmit: handleSubmit,
              })
            : null
        }
        feedback={
          <FeedbackBox state={isGraded ? (correctChosen ? "correct" : "wrong") : isRevealed ? "correct" : "pending"}>
            {isGraded && exercise
              ? correctChosen ? "Got it." : `It was ${correctLabel}.`
              : isRevealed
                ? `It was ${correctLabel}.`
                : isPlaying
                  ? "Listen carefully."
                  : isThinking
                    ? "What did you hear?"
                    : isAwaiting
                      ? "Pick the answer that matches what you heard."
                      : ""}
          </FeedbackBox>
        }
        footer={
          <div className="flex justify-between text-xs text-inkFaded font-mono">
            <span>{mode === "review" ? "graded — updates schedule" : mode === "practice" ? "graded — no schedule change" : "listen"}</span>
            <span>{exercise?.exerciseType ?? ""}</span>
          </div>
        }
      />
    </div>
  );
}
